import React, { createContext, useState, useContext, useEffect } from 'react';
import { reviewAPI } from '../services/api';
import { API_BASE_URL } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [userData, setUserData] = useState(null);

  // Function to validate token
  const validateToken = () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          console.log('Token expired during validation');
          clearAuth();
          return false;
        }
        return true;
      } catch (e) {
        console.error('Error validating token:', e);
        clearAuth();
        return false;
      }
    }
    return false;
  };

  const clearAuth = (isManualLogout = false) => {
    console.log('Clearing authentication state');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setAuthToken(null);
    setUser(null);
    setUserData(null);
  };

  const storeUserData = (data) => {
    console.log('Storing user data:', data);
    setUserData(data);
    setUser(data);
    localStorage.setItem('userData', JSON.stringify(data));
  };

  const refreshUserData = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh user data: ${response.status}`);
      }

      const data = await response.json();
      storeUserData(data);
      return data;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      clearAuth();
      throw error;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      const storedUserData = localStorage.getItem('userData');
      
      if (token && validateToken()) {
        setAuthToken(token);
        
        if (storedUserData) {
          const parsedUserData = JSON.parse(storedUserData);
          if (parsedUserData.role === 'teacher') {
            setUserData(parsedUserData);
            setUser(parsedUserData);
          } else {
            // Not a teacher, clear auth
            clearAuth();
          }
        } else {
          // Try to fetch user data
          try {
            await refreshUserData();
          } catch (error) {
            console.error('Failed to fetch user data on init:', error);
            clearAuth();
          }
        }
      } else {
        clearAuth();
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          identifier: username,
          password: password
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Handle specific error types
        if (data.errorType === 'invalid_user') {
          throw new Error(data.error);
        } else if (data.errorType === 'invalid_password') {
          throw new Error(data.error);
        } else {
          throw new Error(data.error || 'Login failed');
        }
      }

      // Store token
      const token = data.token || data.access_token;
      localStorage.setItem('authToken', token);
      setAuthToken(token);
      
      // Fetch user profile
      const userResponse = await fetch(`${API_BASE_URL}/api/user/profile`, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        },
      });
      
      if (!userResponse.ok) {
        throw new Error('Failed to fetch user profile');
      }
      
      const userDataResponse = await userResponse.json();
      
      // Check if user has access to the teacher review system
      const accessResponse = await fetch(`${API_BASE_URL}/api/user/teacher-access`, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        },
      });

      if (!accessResponse.ok) {
        throw new Error('Failed to check access permissions');
      }

      const accessData = await accessResponse.json();

      if (!accessData.hasAccess) {
        localStorage.removeItem('authToken');
        setAuthToken(null);
        
        // Provide specific error messages based on the reason
        if (accessData.reason === 'not_teacher') {
          throw new Error('Only teachers and admins can access this system');
        } else if (accessData.reason === 'no_assignments') {
          throw new Error('You do not have any essays assigned for review. Please contact an administrator.');
        } else {
          throw new Error('Access denied');
        }
      }
      
      storeUserData(userDataResponse);
      
      return { user: userDataResponse, token };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    clearAuth(true);
  };

  const value = {
    user,
    authToken,
    userData,
    login,
    logout,
    loading,
    refreshUserData,
    storeUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 